import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { fetchWorkspace, updateWorkspace } from "../api/adminClient";
import {
  ASSESSMENT_MODE_FULL_ONLY,
  ASSESSMENT_MODE_LITE_TO_FULL,
} from "./inviteOptions";
import {
  WORKSPACE_COMPANY_MODEL_FLAT,
  WORKSPACE_COMPANY_MODEL_PORTFOLIO,
} from "./inviteConstants";
import {
  LITE_POLICY_DISABLED,
  LITE_POLICY_ENABLED,
  buildProductSettingsPayload,
  workspaceLitePolicyForUi,
  workspaceProductSettings,
} from "./workspaceProductSettings";

/**
 * Workspace-level product settings: organization model + LITE assessment defaults.
 */
export default function WorkspaceProductSettingsPanel({ onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [companyModel, setCompanyModel] = useState(WORKSPACE_COMPANY_MODEL_PORTFOLIO);
  const [litePolicy, setLitePolicy] = useState(LITE_POLICY_DISABLED);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchWorkspace();
      const ws = res.data?.workspace;
      if (!ws) return;
      const ps = workspaceProductSettings(ws);
      setCompanyModel(
        ps.company_model === WORKSPACE_COMPANY_MODEL_FLAT
          ? WORKSPACE_COMPANY_MODEL_FLAT
          : WORKSPACE_COMPANY_MODEL_PORTFOLIO
      );
      setLitePolicy(workspaceLitePolicyForUi(ws));
    } catch (e) {
      toast.error(e.message || "Could not load product settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const liteUiAvailable = litePolicy === LITE_POLICY_ENABLED;

  const handleSave = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const payload = buildProductSettingsPayload({
        companyModel,
        litePolicy,
        defaultAssessmentMode: liteUiAvailable
          ? ASSESSMENT_MODE_LITE_TO_FULL
          : ASSESSMENT_MODE_FULL_ONLY,
        defaultOfferLiteReport: liteUiAvailable ? "Y" : "N",
      });
      await updateWorkspace({
        company_model: payload.company_model,
        product_settings: payload,
      });
      toast.success("Product settings saved");
      await load();
      onSaved?.();
    } catch (err) {
      toast.error(err.message || "Could not save product settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-500">
        Loading product settings…
      </section>
    );
  }

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-5"
      aria-labelledby="workspace-product-settings-heading"
    >
      <div>
        <h2
          id="workspace-product-settings-heading"
          className="text-base font-semibold text-slate-900"
        >
          Product settings
        </h2>
        <p className="text-sm text-slate-600 mt-1">
          Defaults for companies and LITE assessments. Existing workspaces keep prior behavior
          until you change these settings.
        </p>
      </div>

      <form onSubmit={handleSave} className="space-y-5">
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Companies
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:gap-6 text-sm">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="radio"
                name="wsCompanyModel"
                checked={companyModel === WORKSPACE_COMPANY_MODEL_PORTFOLIO}
                onChange={() => setCompanyModel(WORKSPACE_COMPANY_MODEL_PORTFOLIO)}
                disabled={saving}
              />
              <span>
                <span className="font-medium text-slate-900">Portfolio</span>
                <span className="block text-xs text-slate-600">
                  Multiple companies; pick one when inviting participants.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="radio"
                name="wsCompanyModel"
                checked={companyModel === WORKSPACE_COMPANY_MODEL_FLAT}
                onChange={() => setCompanyModel(WORKSPACE_COMPANY_MODEL_FLAT)}
                disabled={saving}
              />
              <span>
                <span className="font-medium text-slate-900">Single organization</span>
                <span className="block text-xs text-slate-600">
                  No company step on invites — one implicit org for the workspace.
                </span>
              </span>
            </label>
          </div>
        </div>

        <div className="space-y-2 border-t border-slate-100 pt-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            LITE assessments
          </p>
          <label className="block text-sm max-w-md">
            <span className="font-medium text-slate-700 mb-1 block">LITE path on new invites</span>
            <select
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              value={litePolicy}
              onChange={(e) => setLitePolicy(e.target.value)}
              disabled={saving}
            >
              <option value={LITE_POLICY_ENABLED}>
                Enabled — LITE assessment and LITE report on invites
              </option>
              <option value={LITE_POLICY_DISABLED}>Disabled — LITE assessment</option>
            </select>
          </label>
          <p className="text-xs text-slate-500">
            Effective now:{" "}
            {liteUiAvailable
              ? "LITE assessment, LITE report, and Studio LITE tools are on."
              : "Full assessment only on invites; Studio hides LITE configuration."}
          </p>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center rounded-lg bg-[#004f73] text-white px-4 py-2 text-sm font-semibold disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save product settings"}
        </button>
      </form>
    </section>
  );
}
